// The per-run LLM stream, carved from
// `src/features/agent-harness/stream/use-agent-stream.ts` (now a delegate).
//
// WHY IT COULD NOT MOVE BEFORE, AND WHAT CHANGED. This hook was the family's
// named `query` blocker twice over: it read `EventDedup` from
// `@/features/agent-events` — the user-wide event mechanism, whose separation was
// this milestone's own precondition — and it defaulted its transport bits to the
// app-side `agentHarnessClient` assembly. The first blocker is gone: `EventDedup`
// moved into this package with the user-wide-events carve and the old path is a
// re-export. The second is what this slice severs.
//
// `streamPath` and `streamHeaders` are now REQUIRED rather than defaulted, for
// the same reason `transport` is required on the run query: the defaults resolve
// the ml-scribe origin and mint auth + client-identity headers, which is app and
// platform state a headless zone may not name. The delegate at the old path
// re-applies both, so every existing call site is unchanged.
//
// `logger` crosses at the platform door (`@/utils/logger/logger` was only ever a
// runtime re-export of it) and `useLatestRef` at `@heidi/utils`, where it was
// lifted; neither was ever a real coupling, and scoring them as one is the
// mistake the delivery notes warn about — read what a `@/` path re-exports, not
// its prefix.
import { fetchEventSource } from '@fortaine/fetch-event-source';
import { useEffect, useRef, useState } from 'react';

import { logger } from '@heidi/runtime/logger';
import { useLatestRef } from '@heidi/utils';

import type {
  LLMStreamConnectionStatus,
  LLMStreamEvent,
  StreamHeadersFactory,
} from '../../../contracts';
import { EventDedup } from '../../events/event-dedup';
import { parseLlmStreamEvent } from './parse-llm-stream-event';
import {
  isStableConnection,
  refreshAuthAfterUnauthorized,
  withClientCapabilities,
} from './reconnect';
import { decideStreamRetry } from './retry-policy';
import { responseError, sleep, StreamHttpError } from './stream-http';
import { compareStreamIds } from './stream-ids';

export interface UseAgentStreamOptions {
  agentRunId: string | null | undefined;
  enabled: boolean;
  /** Open the stream in follow mode (`?follow=true`) so it stays open across turns. */
  follow?: boolean;
  lastEventId?: string | null;
  subscriptionKey?: string | number | null;
  /**
   * REQUIRED, unlike the pre-carve signature. Resolving the run's stream URL
   * needs the deployment's ml-scribe origin, and minting the SSE headers needs
   * the auth token and the client-identity pair — app and platform state. The
   * old-path delegate supplies both from `agentHarnessClient`.
   */
  streamPath: (agentRunId: string) => string;
  streamHeaders: StreamHeadersFactory;
  onEvent: (id: string, event: LLMStreamEvent) => void;
}

export interface UseAgentStreamState {
  status: LLMStreamConnectionStatus;
  lastEventId: string | null;
  error: Error | null;
  retries: number;
}

export const useAgentStream = ({
  agentRunId,
  enabled,
  follow = false,
  lastEventId,
  subscriptionKey,
  streamPath,
  streamHeaders: headersFactory,
  onEvent,
}: UseAgentStreamOptions): UseAgentStreamState => {
  const [status, setStatus] = useState<LLMStreamConnectionStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [retries, setRetries] = useState(0);
  const [deliveredLastEventId, setDeliveredLastEventId] = useState<string | null>(
    lastEventId ?? null
  );
  const onEventRef = useLatestRef(onEvent);
  const cursorRef = useRef<string | null>(lastEventId ?? null);
  const dedupRef = useRef(new EventDedup(256));

  // Re-seed the cursor + dedup only when the run changes — NOT on every
  // snapshot cursor update, and NOT on a same-run re-key: that only forces a
  // reconnect, and rewinding to a lagging snapshot cursor would replay the
  // previous turn's terminal event into the turn that just started.
  const seededRunIdRef = useRef(agentRunId);
  useEffect(() => {
    if (seededRunIdRef.current === agentRunId) return;
    seededRunIdRef.current = agentRunId;
    cursorRef.current = lastEventId ?? null;
    setDeliveredLastEventId(lastEventId ?? null);
    dedupRef.current = new EventDedup(256);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excludes lastEventId; the forward-seed effect below adopts later snapshot cursors without rewinding or wiping the in-connection dedup
  }, [agentRunId]);

  // The snapshot cursor (`lastEventId`) can lead or lag live delivery — polling
  // can return a cursor ahead of what this connection has received. Fast-forward
  // only: adopt it when it's ahead so a reconnect skips snapshot-covered events,
  // but never rewind behind live delivery and never rebuild the dedup mid-stream
  // (that caused redundant replay on reconnect).
  useEffect(() => {
    if (!lastEventId) return;
    if (!cursorRef.current || compareStreamIds(lastEventId, cursorRef.current) > 0) {
      cursorRef.current = lastEventId;
    }
  }, [lastEventId]);

  useEffect(() => {
    if (!enabled || !agentRunId) {
      setStatus('idle');
      setError(null);
      setRetries(0);
      return;
    }

    const ctrl = new AbortController();
    let attempt = 0;

    // Follow mode keeps the per-run stream open across turn boundaries so it
    // delivers turns started on other devices (cross-device sync).
    const url = withClientCapabilities(
      follow ? `${streamPath(agentRunId)}?follow=true` : streamPath(agentRunId)
    );

    const run = async () => {
      let authRefreshed = false;
      while (!ctrl.signal.aborted) {
        let sawBoundary = false;
        let openedAt: number | null = null;
        // The BE emits an explicit `run_terminated` control frame only when the
        // run reached a terminal status. In follow mode a plain close is treated
        // as a transient infra cut (LB idle-cut / GOAWAY) and reconnected; this
        // frame is how we learn the stream will not reopen and stop.
        let sawTerminal = false;
        try {
          setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
          setRetries(attempt);
          setError(null);
          await fetchEventSource(url, {
            method: 'GET',
            headers: headersFactory({ lastEventId: cursorRef.current }),
            signal: ctrl.signal,
            openWhenHidden: true,
            onopen: async (response) => {
              if (!response.ok) {
                throw new StreamHttpError(response.status, await responseError(response));
              }
              openedAt = Date.now();
              authRefreshed = false;
              setStatus('streaming');
            },
            onmessage: (message) => {
              // Idless control frame — carries no cursor, so check it before the
              // id guard and never let it touch dedup/cursor state.
              if (message.event === 'run_terminated') {
                sawTerminal = true;
                return;
              }
              if (!message.id) return;
              const event = parseLlmStreamEvent(message.data);
              if (!event) return;
              if (!dedupRef.current.markSeen(message.id)) return;

              cursorRef.current = message.id;
              setDeliveredLastEventId(message.id);
              attempt = 0;
              setRetries(0);
              if (
                event.type === 'turn_completed' ||
                event.type === 'turn_cancelled' ||
                event.type === 'turn_failed'
              ) {
                sawBoundary = true;
              }
              onEventRef.current(message.id, event);
            },
            onerror: (nextError) => {
              throw nextError instanceof Error ? nextError : new Error(String(nextError));
            },
          });

          if (ctrl.signal.aborted) return;
          // An explicit terminal frame always stops. Otherwise per-turn mode
          // stops at a turn boundary (the stream closes per turn), while follow
          // mode reconnects on any other clean close to survive infra cuts.
          if (sawTerminal || (!follow && sawBoundary)) {
            setStatus('done');
            return;
          }
          throw new Error(
            follow
              ? 'Follow stream closed without a terminal frame; reconnecting'
              : 'Agent stream closed before a turn boundary'
          );
        } catch (nextError) {
          if (ctrl.signal.aborted) return;
          if (isStableConnection(openedAt)) attempt = 0;
          const refreshed =
            !authRefreshed && (await refreshAuthAfterUnauthorized(nextError));
          if (ctrl.signal.aborted) return;
          if (refreshed) authRefreshed = true;
          const decision = decideStreamRetry(nextError, attempt, refreshed);
          if (decision.type === 'stop') {
            setStatus('error');
            setError(decision.error);
            return;
          }
          logger.warn('[agent-harness] LLM stream interrupted; reconnecting', {
            agent_run_id: agentRunId,
            attempt,
            error: decision.error.message,
          });
          attempt = decision.nextAttempt;
          await sleep(decision.delayMs, ctrl.signal);
        }
      }
    };

    void run();

    return () => {
      ctrl.abort();
    };
  }, [
    agentRunId,
    enabled,
    follow,
    headersFactory,
    onEventRef,
    streamPath,
    subscriptionKey,
  ]);

  return { status, lastEventId: deliveredLastEventId, error, retries };
};
